import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  obtenerPrestamoPorCodigoRetiro,
  entregarLibro,
  recibirLibro,
  obtenerTodosLosPrestamosPaginados,
} from "../../../services/PrestamosService";
import {
  obtenerMultaPorPrestamo,
  desactivarMultaPorPrestamo,
} from "../../../services/MultaService";
import { AiOutlineSearch } from "react-icons/ai";
import { useUser } from "../../../context/userContext";
import Swal from "sweetalert2";
import "./prestamos.css";
import { formatearFecha, fmtMoney } from "../../../utils/functions";
import JumpByItemFooter from "../../bibliotecacomponents/customTableFooter/JumpByItemFooter";
import generarVoucherMultaPDF from "../../../utils/pdfmulta";
import { escapeHtml } from "../../../utils/escapeHtml";
import { opcionesCopiasEntrega, etiquetaCopia, formatearFechaHora } from "../../../utils/copiasPrestamo";
import DownloadPrestamosExcel from "../../bibliotecacomponents/downloadPrestamos";

function Prestamos() {
  const { userData } = useUser();
  const [prestamos, setPrestamos] = useState([]);
  const [codigoRetiro, setCodigoRetiro] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pagingMetadata, setPagingMetadata] = useState({});
  const [loading, setLoading] = useState(true);

  const cargarPrestamos = async () => {
    const datosPrestamos = await obtenerTodosLosPrestamosPaginados(
      page,
      pageSize,
    );
    const { content, ...pagingMetadata } = datosPrestamos;
    setPagingMetadata(pagingMetadata);
    setPrestamos(content);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    cargarPrestamos();
  }, [page, pageSize]);

  const handleBuscarPrestamo = async () => {
    try {
      const prestamo = await obtenerPrestamoPorCodigoRetiro(codigoRetiro);
      mostrarAlertaPrestamo(prestamo);
    } catch (error) {
      console.error("Error al buscar el préstamo:", error);
      Swal.fire("Error", "No se encontró el préstamo", "error");
    }
  };

const mostrarAlertaPrestamo = async (prestamo) => {
  // formateador de dinero
  const fmtMoney = (n) =>
    (Number(n) || 0).toLocaleString("es-NI", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });

  let htmlContent;
  let confirmButtonText = "Ok";
  let onConfirm;
  let multa; // sólo se usa en "multado"

  // Ubicaciones seguras
  const ubicacionAprobado = prestamo.libro.ubicacion
    ? `Estante ${prestamo.libro.ubicacion.numEstante}, Repisa ${prestamo.libro.ubicacion.numRepisa}`
    : "desconocida";

  const ubicacionEnEjecucion = prestamo.libro.ubicacion
    ? `Estante ${prestamo.libro.ubicacion.numEstante}, Repisa ${prestamo.libro.ubicacion.numRepisa}`
    : "Ubicación desconocida";

  switch (prestamo.estado) {
    case "aprobado":
      htmlContent = `
        <p><strong>Código de Retiro:</strong> ${prestamo.codigoRetiro}</p>
        <p><strong>Libro:</strong> ${prestamo.libro.titulo}</p>
        <p><strong>Fecha límite de devolución:</strong> ${prestamo.fechaLimiteDevolucion}</p>
        <p><strong>Ubicación:</strong> ${ubicacionAprobado}</p>`;
      confirmButtonText = "Entregar Libro";
      onConfirm = (idCopia) => manejarEntregaLibro(prestamo.idPrestamo, Number(idCopia));
      break;

    case "en ejecución":
      htmlContent = `
        <p><strong>Código de Retiro:</strong> ${prestamo.codigoRetiro}</p>
        <p><strong>Recuerde ubicar el libro en:</strong> ${ubicacionEnEjecucion}</p>`;
      confirmButtonText = "Recibir Libro";
      onConfirm = () => manejarRecepcionLibro(prestamo.idPrestamo);
      break;

    case "finalizado":
      htmlContent = "<p>Este préstamo ya ha finalizado.</p>";
      break;

    case "multado":
      multa = await obtenerMultaPorPrestamo(prestamo.idPrestamo);
      htmlContent = `
        <p>Este préstamo está multado.</p>
        <p><strong>Monto de la Multa:</strong> ${fmtMoney(multa.montoMulta)}</p>`;
      confirmButtonText = "Cancelar Multa";
      onConfirm = () => manejarCancelarMulta(prestamo);
      break;

    default:
      htmlContent = "<p>No se encontró información del préstamo.</p>";
  }

  htmlContent += `<p><strong>${prestamo.estado === "aprobado" ? "Copia reservada" : "Copia"}:</strong> ${escapeHtml(etiquetaCopia(prestamo))}</p>
    <p><strong>Fecha de entrega:</strong> ${escapeHtml(formatearFechaHora(prestamo.fechaEntrega))}</p>
    <p><strong>Fecha de recepción:</strong> ${escapeHtml(formatearFechaHora(prestamo.fechaRecepcion))}</p>`;

  // configuración base del modal
  const baseOpts = {
    title: "Detalles del Préstamo",
    html: htmlContent,
    ...(prestamo.estado === "aprobado" ? {
      input: "select",
      inputLabel: "Código de la copia física que se entrega",
      inputOptions: opcionesCopiasEntrega(prestamo),
      inputValue: prestamo.idCopia ? String(prestamo.idCopia) : "",
      inputPlaceholder: "Seleccioná la copia",
      inputValidator: (value) => !value ? "Seleccioná una copia antes de entregarla." : undefined,
    } : {}),
    confirmButtonText: confirmButtonText,
    confirmButtonColor: "#21807C",
    preConfirm: onConfirm,
    showLoaderOnConfirm: prestamo.estado !== "finalizado",
    allowOutsideClick: () => !Swal.isLoading(),
    // estilos modernos para acciones
    customClass: {
      actions: "swal-actions-modern",
      confirmButton: "swal-btn-confirm",
      denyButton: "swal-btn-secondary",
    },
    // usar la X para cerrar (más limpio visualmente)
    showCloseButton: true,
  };

  let result;

  if (prestamo.estado === "multado") {
    // En multa: dos botones abajo (Confirm y Deny) + X para cerrar
    result = await Swal.fire({
      ...baseOpts,
      showCancelButton: false,
      showDenyButton: true,
      denyButtonText: "📄 Voucher",
      denyButtonColor: "gray", // gris
    });

    if (result.isDenied) {
      generarVoucherMultaPDF({
        multa,
        prestamo,
        emitidoPor: userData?.nombreCompleto || userData?.username || "Bibliotecario",
      });
    }
  } else if (prestamo.estado === "finalizado") {
    // Finalizado: solo botón Cerrar (X)
    await Swal.fire({
      ...baseOpts,
      showCancelButton: false,
      showConfirmButton: false, // sólo X
    });
  } else {
    // Aprobado / En ejecución: Confirm + X (sin tercer botón)
    await Swal.fire({
      ...baseOpts,
      showCancelButton: false,
      showDenyButton: false,
    });
  }
};







  const manejarEntregaLibro = async (idPrestamo, idCopia) => {
    try {
      await entregarLibro(idPrestamo, userData.id, idCopia);
      Swal.fire({
        title: "Éxito!",
        text: "El libro ha sido entregado con éxito.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      setCodigoRetiro("");
      cargarPrestamos();
    } catch (error) {
      const mensajeError =
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
          ? error.response.data.message
          : error.response.data;
      Swal.showValidationMessage(mensajeError || "No se pudo entregar esta copia.");
      return false;
    }
  };

  const manejarRecepcionLibro = async (idPrestamo) => {
    try {
      await recibirLibro(idPrestamo, userData.id);
      Swal.fire({
        title: "Éxito!",
        text: "El libro ha sido recibido con éxito.",
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      cargarPrestamos();
      setCodigoRetiro("");
    } catch (error) {
      const mensajeError =
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
          ? error.response.data.message
          : error.response.data;
      Swal.fire({
        title: "Error!",
        text: mensajeError,
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

  const manejarCancelarMulta = async (prestamo) => {
    try {
      await desactivarMultaPorPrestamo(prestamo.idPrestamo, userData.id);
      // Si la ubicación es null o undefined, usa 'Ubicación desconocida'
      const ubicacionEnEjecucion = prestamo.libro.ubicacion
      ? `Estante ${prestamo.libro.ubicacion.numEstante}, Repisa ${prestamo.libro.ubicacion.numRepisa}`
      : "Ubicación desconocida";

      const htmlContent = `
        <p>Recuerde tomar el libro del usuario y colocarlo en su lugar.</p>
        <p><strong>Recuerde ubicar el libro en:</strong> ${ubicacionEnEjecucion}</p>`;

      Swal.fire({
        title: "¡La multa ha sido cancelada con éxito!",
        html: htmlContent,
        icon: "success",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
      cargarPrestamos();
    } catch (error) {
      const mensajeError =
        error.response &&
        error.response.data &&
        typeof error.response.data === "object"
          ? error.response.data.message
          : error.response.data;

      Swal.fire({
        title: "Error!",
        text: mensajeError,
        icon: "error",
        confirmButtonText: "Ok",
        confirmButtonColor: "#21807C",
      });
    }
  };

  const columnas = [
    {
      field: "idPrestamo",
      headerName: "ID",
      width: 100,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
    },
    {
      field: "estudiante",
      headerName: "Cif Estudiante",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => `${params.row.usuario.cif}`,
    },
    {
      field: "estudianteNombre",
      headerName: "Nombre del Estudiante",
      width: 280,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => `${params.row.usuario.nombreCompleto}`,
    },
    {
      field: "libro",
      headerName: "Libro",
      width: 260,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => `${params.row.libro.titulo}`,
    },
    {
      field: "codigoCopia",
      headerName: "Código de Copia",
      width: 190,
      valueGetter: (params) => etiquetaCopia(params.row),
    },
    {
      field: "fechaEntrega",
      headerName: "Fecha de entrega",
      width: 220,
      valueGetter: (params) => formatearFechaHora(params.row.fechaEntrega),
    },
    {
      field: "fechaRecepcion",
      headerName: "Fecha de recepción",
      width: 220,
      valueGetter: (params) => formatearFechaHora(params.row.fechaRecepcion),
    },
    {
      field: "fechaSolicitud",
      headerName: "Fecha de solicitud",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => formatearFecha(params.row.fechaSolicitud),
    },
    {
      field: "fechaLimiteDevolucion",
      headerName: "Fecha límite de devolución",
      width: 260,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => formatearFecha(params.row.fechaLimiteDevolucion),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 180,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      renderCell: (params) => (
        <>
          <div style={makeStyle(params.row.estado)}>
            {params.row.estado.charAt(0).toUpperCase() +
              params.row.estado.slice(1)}
          </div>
        </>
      ),
    },
    {
      field: "renovaciones",
      headerName: "Num. Renovaciones",
      width: 180,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) => `${params.row.numRenovaciones}`,
    },
    {
      field: "personalEntrega",
      headerName: "Personal de Entrega",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) =>
        params.row.personalEntrega
          ? params.row.personalEntrega.nombreCompleto
          : "--",
    },
    {
      field: "personalRecepcion",
      headerName: "Personal de Recepción",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
      valueGetter: (params) =>
        params.row.personalRecepcion
          ? params.row.personalRecepcion.nombreCompleto
          : "--",
    },
    {
      field: "codigoRetiro",
      headerName: "Código de Retiro",
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "tablaInventario-headers",
    },
    // Agrega más columnas según tus necesidades
  ];

  const makeStyle = (status) => {
    if (status === "en ejecución") {
      return {
        background: "rgb(145 254 159 / 47%)",
        color: "green",
        padding: "5px",
        borderRadius: "8px",
      };
    } else if (status === "multado") {
      return {
        background: "#ffadad8f",
        color: "red",
        padding: "5px",
        borderRadius: "8px",
      };
    } else if (status === "aprobado") {
      return {
        background: "#59bfff",
        color: "white",
        padding: "5px",
        borderRadius: "8px",
      };
    } else {
      return {
        background: "#C1C1C1",
        color: "white",
        padding: "5px",
        borderRadius: "8px",
      };
    }
  };

    return (
      <div className="prestamosContainer">
        <h1>Préstamos</h1>
        <div className="prestamos-Top">
          <div className="inventario-searchBar">
            <AiOutlineSearch size={20} style={{ color: "#909090" }} />
            <input
              type="text"
              placeholder="Código de aprobación"
              value={codigoRetiro}
              onChange={(e) => setCodigoRetiro(e.target.value)}
            />
          </div>
          <button onClick={handleBuscarPrestamo}>Entregar/Recibir</button>
          <div style={{ display: "flex", justifyContent: "flex-end", width: "100%"}}>
            <DownloadPrestamosExcel />
          </div>
        </div>
        <div className="tablaPrestamosContainer">
          <DataGrid
            rows={prestamos}
            columns={columnas}
            rowCount={pagingMetadata.totalElements ?? 0}
            loading={loading}
            paginationMode="server"
            paginationModel={{
              pageSize,
              page,
            }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
            }}
            getRowId={(row) => row.idPrestamo}
            rowHeight={80}
            slots={{ footer: JumpByItemFooter }}
            slotProps={{
              footer: {
                total: pagingMetadata.totalElements ?? 0,
                page,
                pageSize,
                onJumpPage: (p) => setPage(p),
                noun: "préstamo",
              },
            }}
          />
        </div>
      </div>
    );

}

export default Prestamos;
